import { Alert, Card, Descriptions, Empty, Space, Spin, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSettingsList } from "@/modules/settings/hooks/useSettings";
import { DataTable } from "@/shared/components/DataTable";
import { ConfirmModal } from "../components/ConfirmModal";
import { RunCard } from "../components/RunCard";
import {
	useCancelRun,
	useConfirmRun,
	useOchiqYugurishlar,
	useYugurishTarixi,
} from "../hooks/useRetention";
import {
	baytFormat,
	kindLabel,
	OCHIQ_HOLATLAR,
	type RetentionRun,
	STATUS_RANGI,
	statusLabel,
} from "../types";

const { Title, Text } = Typography;

/** Sozlamalar xulosasida ko'rsatiladigan kalitlar va ularning i18n kalitlari. */
const SOZLAMA_KALITLARI: { key: string; labelKey: string; unitKey?: "months" | "hours" }[] = [
	{ key: "retention_location_months", labelKey: "retentionLocationMonths", unitKey: "months" },
	{ key: "retention_photo_months", labelKey: "retentionPhotoMonths", unitKey: "months" },
	{ key: "retention_orphan_min_age_h", labelKey: "retentionOrphanMinAgeH", unitKey: "hours" },
	{ key: "retention_auto_confirm", labelKey: "retentionAutoConfirm" },
];

function sozlamaMatni(
	t: (key: string, options?: Record<string, unknown>) => string,
	value: unknown,
	unitKey?: "months" | "hours"
): string {
	if (typeof value === "boolean") {
		return value ? t("page.settings.boolOn") : t("page.settings.boolOff");
	}

	if (value === null || value === undefined) {
		return "—";
	}

	return unitKey ? `${String(value)} ${t(`page.settings.units.${unitKey}`)}` : String(value);
}

/**
 * ⚠️ **Sozlamalar xulosasi FAQAT O'QISH uchun** — o'zgartirish
 * `/settings` sahifasida. Ikki joyda tahrirlash bir sozlama uchun ikki
 * haqiqat manbai hosil qilardi.
 *
 * ⚠️ Sozlamalar so'rovi yiqilsa BUTUN sahifa bo'shab qolmaydi:
 * yugurishlar ro'yxati mustaqil so'rov va u ko'rinishda qoladi.
 */
function SozlamalarXulosasi() {
	const { t } = useTranslation("retention");
	const { data, isLoading, isError } = useSettingsList();

	if (isError) {
		return (
			<Alert
				type="warning"
				showIcon
				title={t("page.settings.loadErrorTitle")}
				description={t("page.settings.loadErrorDescription")}
			/>
		);
	}

	if (isLoading) {
		return <Spin size="small" />;
	}

	const xarita = new Map((data ?? []).map((qator) => [qator.key, qator.value]));

	return (
		<Card size="small" title={t("page.settings.title")}>
			<Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
				{SOZLAMA_KALITLARI.map((sozlama) => (
					<Descriptions.Item
						key={sozlama.key}
						label={t(`page.settings.labels.${sozlama.labelKey}`)}
					>
						{sozlamaMatni(t, xarita.get(sozlama.key), sozlama.unitKey)}
					</Descriptions.Item>
				))}
			</Descriptions>
			<Text type="secondary" className="!text-xs">
				{t("page.settings.footnote")}
			</Text>
		</Card>
	);
}

/**
 * Ma'lumot saqlash muddati sahifasi (4-bosqich reja Task 7).
 *
 * Tuzilishi:
 * ```
 * Sarlavha + sozlamalar xulosasi (faqat o'qish)
 * ─────────────────────────────────────────────
 * Ochiq yugurishlar (dry_run / confirmed / running) — kartalar
 * ─────────────────────────────────────────────
 * Tarix (done / failed) — jadval
 * ```
 *
 * ⚠️⚠️ **Bu sahifa HECH NARSA O'CHIRMAYDI.** Tasdiqlash faqat
 * `retention_runs.status` ni belgilaydi; haqiqiy `DROP TABLE` va obyekt
 * o'chirish worker'ning keyingi tikida bajariladi (kontrakt §7.2).
 * Shuning uchun muvaffaqiyat xabari «bajarildi» EMAS, «keyingi tikda
 * bajariladi» deydi.
 */
export function RetentionPage() {
	const { t } = useTranslation("retention");
	const ochiq = useOchiqYugurishlar();
	const tarix = useYugurishTarixi();
	const confirmMutation = useConfirmRun();
	const cancelMutation = useCancelRun();

	const [modal, setModal] = useState<{ run: RetentionRun; mode: "confirm" | "cancel" } | null>(
		null
	);

	const yopish = () => setModal(null);

	const yuborish = (reason: string) => {
		if (!modal) {
			return;
		}

		const mutation = modal.mode === "confirm" ? confirmMutation : cancelMutation;

		mutation.mutate({ id: modal.run.id, reason }, { onSuccess: yopish });
	};

	/**
	 * ⚠️ Tarix jadvali server tomonidan sahifalanadi va HAMMA holatni
	 * qaytaradi — ochiq yugurishlar yuqorida kartada ko'ringani uchun
	 * mijoz tomonida chiqarib tashlanadi.
	 *
	 * ⚠️ **Oqibati ANIQ va CHEGARALANGAN, jimgina emas:** filtr
	 * sahifalashdan KEYIN qo'llanadi, ya'ni birinchi sahifada
	 * ko'rsatilgan qatorlar soni `meta.total` dan kamroq bo'lishi
	 * mumkin. Farq eng ko'p **3 ta** — `retention_runs_open_uniq`
	 * qisman unique indeksi tur boshiga aynan bitta ochiq yugurishga
	 * ruxsat beradi va turlar uchta. Backendga `status NOT IN (...)`
	 * filtri qo'shish buni yopardi, lekin kontrakt §6.4 marshrut
	 * shaklini muzlatgan (`kind` va `status` — aynan tenglik).
	 */
	const tarixQatorlari = tarix.items.filter((run) => !OCHIQ_HOLATLAR.includes(run.status));

	const tarixUstunlari: ColumnsType<RetentionRun> = [
		{
			title: t("page.history.columns.kind"),
			dataIndex: "kind",
			key: "kind",
			render: (kind: string) => kindLabel(t, kind),
		},
		{
			title: t("page.history.columns.status"),
			dataIndex: "status",
			key: "status",
			render: (status: RetentionRun["status"]) => (
				<Tag color={STATUS_RANGI[status]}>{statusLabel(t, status)}</Tag>
			),
		},
		{
			title: t("page.history.columns.deletedCount"),
			dataIndex: "deletedCount",
			key: "deletedCount",
		},
		{
			title: t("page.history.columns.bytesFreed"),
			dataIndex: "bytesFreed",
			key: "bytesFreed",
			render: (bytes: number) => baytFormat(bytes),
		},
		{
			title: t("page.history.columns.reason"),
			dataIndex: "reason",
			key: "reason",
			render: (reason: string | null) => reason ?? "—",
		},
		{
			title: t("page.history.columns.finishedAt"),
			dataIndex: "finishedAt",
			key: "finishedAt",
			render: (at: string | null) => (at ? new Date(at).toLocaleString("uz-UZ") : "—"),
		},
		{
			title: t("page.history.columns.error"),
			dataIndex: "error",
			key: "error",
			render: (error: string | null) => (error ? <Text type="danger">{error}</Text> : "—"),
		},
	];

	return (
		<Space direction="vertical" size="large" className="!w-full">
			<div>
				<Title level={3} className="!mb-1">
					{t("page.title")}
				</Title>
				<Text type="secondary">{t("page.subtitle")}</Text>
			</div>

			<SozlamalarXulosasi />

			<div>
				<Title level={5}>{t("page.openRuns.title")}</Title>
				{ochiq.isLoading ? (
					<Spin />
				) : ochiq.isError ? (
					<Alert type="error" showIcon title={t("page.openRuns.loadError")} />
				) : (ochiq.data ?? []).length === 0 ? (
					<Empty description={t("page.openRuns.empty")} />
				) : (
					<Space direction="vertical" size="middle" className="!w-full">
						{(ochiq.data ?? []).map((run) => (
							<RunCard
								key={run.id}
								run={run}
								onConfirm={(target) => setModal({ run: target, mode: "confirm" })}
								onCancel={(target) => setModal({ run: target, mode: "cancel" })}
							/>
						))}
					</Space>
				)}
			</div>

			<div>
				<Title level={5}>{t("page.history.title")}</Title>
				{tarixQatorlari.length === 0 && !tarix.isLoading ? (
					<Empty description={t("page.history.empty")} />
				) : (
					<DataTable<RetentionRun>
						columns={tarixUstunlari}
						query={{ ...tarix, items: tarixQatorlari }}
					/>
				)}
			</div>

			<ConfirmModal
				open={modal !== null}
				run={modal?.run ?? null}
				mode={modal?.mode ?? "confirm"}
				loading={confirmMutation.isPending || cancelMutation.isPending}
				onCancel={yopish}
				onSubmit={yuborish}
			/>
		</Space>
	);
}
